import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';

import { Divider } from '@/components/Divider';
import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { PlusCircleIcon } from '@/components/icons/PlusCircleIcon';
import { XCircleIcon } from '@/components/icons/XCircleIcon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { TableHeaderBar } from '@/components/tables/components/TableHeaderBar/TableHeaderBar';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.constants';
import {
  AIOnboardingModalFormShape,
  AIOnboardingModalFormShapeKeys,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { AIOnboardingSuggestionActions } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingSuggestionActions';
import { AssetFieldsRow } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/assets.fields';
import { getEmptyAssetField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/assets.fields.utils';
import { EntityFieldsRow } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/entities.fields';
import { getEmptyEntityField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/entities.fields.utils';
import { IndividualFieldsRow } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/individuals.fields';
import { getEmptyIndividualField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/individuals.fields.utils';
import { LiabilityFieldsRow } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/liabilities.fields';
import { getEmptyLiabilityField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/liabilities.fields.utils';
import { LifeInsurancePolicyFieldsRow } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/lifeInsurancePolicies.fields';
import { getEmptyLifeInsurancePolicyField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/lifeInsurancePolicies.fields.utils';
import { OrganizationFieldsRow } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/organizations.fields';
import { getEmptyOrganizationField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/organizations.fields.utils';
import { TestamentaryEntityFieldsRow } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/testamentaryEntities.fields';
import { getEmptyTestamentaryEntityField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/testamentaryEntities.fields.utils';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { useSelectedSuggestionEvidenceContext } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.context';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';

interface AIOnboardingModalFormTableProps {
  header: string;
  formPath: AIOnboardingModalFormShapeKeys;
  onAcceptSuggestion: (params: {
    formPath: AIOnboardingModalFormShapeKeys;
    suggestionID: string;
  }) => Promise<boolean>;
  onRejectSuggestion: (suggestionID: string) => Promise<boolean>;
  beginOperation: () => boolean;
  endOperation: () => void;
  disabled: boolean;
  pendingSuggestionIDs: string[];
}

export function AIOnboardingModalFormTable({
  header,
  formPath,
  onAcceptSuggestion,
  onRejectSuggestion,
  beginOperation,
  endOperation,
  disabled,
  pendingSuggestionIDs,
}: AIOnboardingModalFormTableProps) {
  const trackUserEvent = useTrackUserEvent();

  const { onSuggestionSaved, reviewedSuggestionIDs, suggestionsByID } =
    useAIOnboardingModalContext();

  const { locateSuggestion } = useSelectedSuggestionEvidenceContext();
  const { control, getValues } = useFormContext<AIOnboardingModalFormShape>();

  const { fields, remove, prepend } = useFieldArray({
    control,
    name: formPath,
  });

  const { columnPaddingHorizontal } =
    AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS[formPath].styles;

  const hiddenSuggestionIDs = useMemo(
    () => new Set([...pendingSuggestionIDs, ...reviewedSuggestionIDs]),
    [pendingSuggestionIDs, reviewedSuggestionIDs]
  );
  const visibleFieldCount = fields.filter(
    (field) =>
      !field._suggestionID || !hiddenSuggestionIDs.has(field._suggestionID)
  ).length;

  const columns = useMemo(
    () =>
      Object.values(
        AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS[formPath].columns
      ).filter((c) => c.columnHeader),
    [formPath]
  );

  const handleRemoveRow = useCallback(
    (fieldId: string) => {
      const index = fields.findIndex((field) => field.id === fieldId);
      const field = fields[index];
      if (index === -1 || !field) {
        diagnostics.warn(
          `Trying to remove fieldArrayId=${fieldId} but it does not exist in the AIOnboarding.${formPath} form`
        );
        return;
      }
      remove(index);
      trackUserEvent('ai_onboarding delete suggested row', {
        formPath,
      });
    },
    [fields, formPath, remove, trackUserEvent]
  );

  const handleCreatedFromSuggestion = useCallback(
    (fieldId: string) => {
      const index = fields.findIndex((field) => field.id === fieldId);
      if (index === -1) {
        diagnostics.warn(
          `Trying to remove fieldArrayId=${fieldId} but it does not exist in the AIOnboarding.${formPath} form`
        );
        return;
      }
      remove(index);
    },
    [fields, formPath, remove]
  );

  const handleAcceptRow = useCallback(
    async (fieldID: string) => {
      const suggestionID = fields.find(
        (candidate) => candidate.id === fieldID
      )?._suggestionID;
      if (!suggestionID) {
        diagnostics.warn(
          `Trying to accept fieldArrayId=${fieldID} but it does not exist in the AIOnboarding.${formPath} form`
        );
        return;
      }

      const didAccept = await onAcceptSuggestion({ formPath, suggestionID });
      if (!didAccept) {
        return;
      }

      const currentIndex = getValues(formPath).findIndex(
        (row) => row._suggestionID === suggestionID
      );
      if (currentIndex !== -1) {
        remove(currentIndex);
      }
    },
    [fields, formPath, getValues, onAcceptSuggestion, remove]
  );

  const handleRejectRow = useCallback(
    async (fieldID: string) => {
      const suggestionID = fields.find(
        (candidate) => candidate.id === fieldID
      )?._suggestionID;
      if (!suggestionID) {
        diagnostics.warn(
          `Trying to reject fieldArrayId=${fieldID} but it does not exist in the AIOnboarding.${formPath} form`
        );
        return;
      }

      const didReject = await onRejectSuggestion(suggestionID);
      if (!didReject) {
        return;
      }

      const currentIndex = getValues(formPath).findIndex(
        (row) => row._suggestionID === suggestionID
      );
      if (currentIndex !== -1) {
        remove(currentIndex);
      }
    },
    [fields, formPath, getValues, onRejectSuggestion, remove]
  );

  const handleMarkedDuplicate = useCallback(
    (suggestionID: string) => {
      onSuggestionSaved(suggestionID);
      const currentIndex = getValues(formPath).findIndex(
        (row) => row._suggestionID === suggestionID
      );
      if (currentIndex !== -1) {
        remove(currentIndex);
      }
    },
    [formPath, getValues, onSuggestionSaved, remove]
  );

  const handleAddRow = useCallback(() => {
    trackUserEvent('ai_onboarding add row', {
      formPath,
    });

    switch (formPath) {
      case 'individuals':
        prepend(getEmptyIndividualField());
        break;
      case 'organizations':
        prepend(getEmptyOrganizationField());
        break;
      case 'entities':
        prepend(getEmptyEntityField());
        break;
      case 'testamentaryEntities':
        prepend(getEmptyTestamentaryEntityField());
        break;
      case 'assets':
        prepend(getEmptyAssetField());
        break;
      case 'liabilities':
        prepend(getEmptyLiabilityField());
        break;
      case 'lifeInsurancePolicies':
        prepend(getEmptyLifeInsurancePolicyField());
        break;
      default:
        throw new UnreachableError({
          case: formPath,
          message:
            'Unknown formPath in AIOnboardingModalFormTable.handleAddRow',
        });
    }
  }, [formPath, prepend, trackUserEvent]);

  const handleSearchEvidence = useCallback(
    (suggestionID: string | undefined) => {
      if (!suggestionID || !suggestionsByID[suggestionID]) {
        diagnostics.warn(
          `Trying to search evidence for suggestionID=${suggestionID} but it does not exist`
        );
        return;
      }
      locateSuggestion(suggestionsByID[suggestionID]!);
    },
    [locateSuggestion, suggestionsByID]
  );

  return (
    <Stack data-testid={`onboarding-suggestion-table-${formPath}`}>
      <TableHeaderBar
        header={header}
        variant="title-s"
        rightContent={
          <Button
            size="xs"
            variant="transparent-inverse"
            startIcon={PlusCircleIcon}
            onClick={handleAddRow}
            disabled={disabled}
            data-testid="onboarding-suggestion-table-addRow-button"
          >
            Manually add
          </Button>
        }
      />
      <Stack direction="row" p={1.5} bgcolor={COLORS.GRAY['100']}>
        <Stack width="100%">
          <FormLayoutRow
            sx={{
              gap: columnPaddingHorizontal,
            }}
          >
            {columns.map(
              ({ columnHeader, columnWidth, columnHeaderTooltip }) => (
                <FormLayoutItem
                  key={columnHeader}
                  width={columnWidth}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="title-xs" color={COLORS.GRAY['500']}>
                    {columnHeader}
                  </Typography>
                  {columnHeaderTooltip && (
                    <ContextualHelpTooltip>
                      <PopperContent body={columnHeaderTooltip} />
                    </ContextualHelpTooltip>
                  )}
                </FormLayoutItem>
              )
            )}
          </FormLayoutRow>
        </Stack>
      </Stack>
      <Stack
        divider={
          <Divider sx={{ backgroundColor: COLORS.GRAY['200'] }} noMargin />
        }
      >
        {fields.map((field, index) => {
          // Keep the row in the form while its request runs. If the optimistic
          // action fails, we can show the row again with its edited values.
          if (
            field._suggestionID &&
            hiddenSuggestionIDs.has(field._suggestionID)
          ) {
            return null;
          }

          return (
            <AIOnboardingModalFormRow
              key={field.id}
              index={index}
              formPath={formPath}
              suggestionID={field._suggestionID}
              onEvidenceClick={() => handleSearchEvidence(field._suggestionID)}
              onRemoveClick={() => handleRemoveRow(field.id)}
              onAcceptClick={() => handleAcceptRow(field.id)}
              onRejectClick={() => handleRejectRow(field.id)}
              onMarkedDuplicate={handleMarkedDuplicate}
              beginOperation={beginOperation}
              endOperation={endOperation}
              disabled={disabled}
              onCreatedFromSuggestion={() =>
                handleCreatedFromSuggestion(field.id)
              }
            />
          );
        })}
        {visibleFieldCount === 0 && (
          <Typography
            variant="body-xs"
            ml={7}
            mt={2.5}
            color={COLORS.GRAY['500']}
          >
            No remaining suggestions
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

interface AIOnboardingModalFormRowProps {
  index: number;
  formPath: AIOnboardingModalFormShapeKeys;
  suggestionID: string | undefined;
  onEvidenceClick: () => void;
  onRemoveClick: () => void;
  onAcceptClick: () => void;
  onRejectClick: () => void;
  onMarkedDuplicate: (suggestionID: string) => void;
  beginOperation: () => boolean;
  endOperation: () => void;
  disabled: boolean;
  onCreatedFromSuggestion: () => void;
}

export function AIOnboardingModalFormRow({
  index,
  formPath,
  suggestionID,
  onEvidenceClick,
  onRemoveClick,
  onAcceptClick,
  onRejectClick,
  onMarkedDuplicate,
  beginOperation,
  endOperation,
  disabled,
  onCreatedFromSuggestion,
}: AIOnboardingModalFormRowProps) {
  const { suggestionsByID } = useAIOnboardingModalContext();
  const { rowPaddingVertical } =
    AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS[formPath].styles;
  const suggestion = suggestionID ? suggestionsByID[suggestionID] : undefined;

  const rowActions = useMemo(() => {
    if (suggestion) {
      const parentTrustName =
        suggestion.onboardingTestamentaryParentKgV2Node?.namePrimary;
      return (
        <AIOnboardingSuggestionActions
          suggestion={suggestion}
          locationContext={
            parentTrustName ? `From ${parentTrustName}` : undefined
          }
          onEvidenceClick={onEvidenceClick}
          onAccept={onAcceptClick}
          onReject={onRejectClick}
          onMarkedDuplicate={onMarkedDuplicate}
          beginOperation={beginOperation}
          endOperation={endOperation}
          disabled={disabled}
        />
      );
    }

    return (
      <IconButton
        size="xs"
        icon={XCircleIcon}
        onClick={onRemoveClick}
        disabled={disabled}
        ariaLabel="Remove row"
        sx={{
          alignSelf: 'flex-end',
          color: COLORS.GRAY['500'],
          border: 'none',
        }}
        data-testid="onboarding-suggestion-row-remove-button"
      />
    );
  }, [
    beginOperation,
    disabled,
    endOperation,
    onAcceptClick,
    onEvidenceClick,
    onMarkedDuplicate,
    onRejectClick,
    onRemoveClick,
    suggestion,
  ]);

  const row = useMemo(() => {
    switch (formPath) {
      case 'individuals':
        return (
          <IndividualFieldsRow
            index={index}
            onCreatedFromSuggestion={onCreatedFromSuggestion}
          />
        );
      case 'organizations':
        return <OrganizationFieldsRow index={index} />;
      case 'entities':
        return <EntityFieldsRow index={index} suggestionID={suggestionID} />;
      case 'testamentaryEntities':
        return (
          <TestamentaryEntityFieldsRow
            index={index}
            suggestionID={suggestionID}
          />
        );
      case 'assets':
        return <AssetFieldsRow index={index} />;
      case 'liabilities':
        return <LiabilityFieldsRow index={index} />;
      case 'lifeInsurancePolicies':
        return <LifeInsurancePolicyFieldsRow index={index} />;
    }
  }, [formPath, index, suggestionID, onCreatedFromSuggestion]);

  return (
    <Stack
      sx={{
        pt: rowPaddingVertical,
        pb: 2, // Always use 2 for bottom padding.
      }}
      data-testid="onboarding-suggestion-row"
    >
      <Stack px={1.5} mb={0.5}>
        {rowActions}
      </Stack>
      <Stack width="100%" gap={1} px={1.5}>
        {row}
      </Stack>
    </Stack>
  );
}
